import type { DropdownOption } from "@modernatx-internal/component-library";
import { Box, Button, Divider, Dropdown, Text } from "@modernatx-internal/component-library";
import React from "react";

import type { SearchProps } from "@/content-V2/types";
import type { Vaccine } from "@/finder/hooks/V2/useFinder";
import { useFinder } from "@/finder/hooks/V2/useFinder";
import { useLocationSearch } from "@/finder/hooks/V2/useLocationSearch";

import { LocationSearchBar } from "./LocationSearchBar";

export const Search: React.FC<SearchProps> = (props) => {
  const {
    clientGeolocationErrorMessages,
    detectLocationDropdownLabel,
    header,
    preSearchPrompt,
    invalidLocationErrorMessage,
    searchInputLabel,
    showMobileSearchButton = false,
    vaccineDropdownOptions
  } = props;

  const showVaccineDropdown = !!vaccineDropdownOptions && vaccineDropdownOptions.length > 0;

  const {
    formRef,
    locationSearchOptionSet,
    showSearchPrompt,
    searchValue,
    searchValueSet,
    selectedVaccines,
    selectedVaccinesSet,
    markUserFormInteraction,
    vaccineValidationError
  } = useFinder();
  const {
    handleOnBlur,
    handleSearchChange,
    handleSearchSubmit,
    handleSearchSuggestionClick,
    searchSuggestionsList,
    searchError
  } = useLocationSearch({
    clientGeolocationErrorMessages,
    detectLocationDropdownLabel,
    invalidLocationErrorMessage,
    searchOptionSelect: locationSearchOptionSet,
    searchValue,
    searchValueSet,
    markUserFormInteraction
  });
  const [internalVaccines, internalVaccinesSet] = React.useState<Vaccine[] | undefined>(
    selectedVaccines
  );

  // Debounce the vaccines input to avoid too many state updates
  React.useEffect(() => {
    if (selectedVaccinesSet && internalVaccines && selectedVaccines) {
      const timer = setTimeout(() => {
        if (internalVaccines.join(",") !== selectedVaccines.join(",")) {
          selectedVaccinesSet(internalVaccines);
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [internalVaccines, selectedVaccines, selectedVaccinesSet]);

  // Update the internal vaccines state when the selectedVaccines prop changes
  // This is to ensure that the internal state is in sync with the prop
  React.useEffect(() => {
    internalVaccinesSet(selectedVaccines);
  }, [selectedVaccines]);

  const vaccineValue = React.useMemo(() => {
    if (!vaccineDropdownOptions || !selectedVaccines) {
      return [];
    }
    const selectedOptions = vaccineDropdownOptions?.filter((option) =>
      selectedVaccines.includes(option.value)
    );

    return selectedOptions;
  }, [vaccineDropdownOptions, selectedVaccines]);

  const handleVaccineSelect = (options: DropdownOption[]) => {
    internalVaccinesSet(options.map((option) => option.value as Vaccine));
    markUserFormInteraction();
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: ["initial", null, "radius-2"],
        display: "flex",
        flexDirection: "column",
        gap: ["spacing-3", null, "spacing-5"],
        px: "spacing-5",
        py: ["spacing-3", null, "spacing-5"]
      }}
    >
      <Text size="font-size-6" weight="bold" sx={{ color: "color-current-brand-800" }}>
        {header}
      </Text>
      <Box
        ref={formRef}
        as="form"
        data-track="finder-search-bar"
        sx={{ display: "flex", flexDirection: "column", gap: ["spacing-3", null, "spacing-5"] }}
      >
        <LocationSearchBar
          searchInputLabel={searchInputLabel}
          handleOnBlur={handleOnBlur}
          handleSearchChange={handleSearchChange}
          handleSearchSubmit={handleSearchSubmit}
          handleSearchSuggestionClick={handleSearchSuggestionClick}
          searchSuggestionsList={searchSuggestionsList}
          searchError={searchError}
        />
        {showVaccineDropdown && (
          <Box sx={{ display: "flex", gap: "spacing-5" }}>
            <Box sx={{ flex: 1 }}>
              <Dropdown
                key="dropdown-vaccine"
                multiSelect={true}
                error={vaccineValidationError}
                selectedOptions={vaccineValue}
                label="Vaccine"
                dropdownOptions={vaccineDropdownOptions}
                wrapOptions={true}
                menuMaxHeight="none"
                onSelectOptions={handleVaccineSelect}
              />
            </Box>
          </Box>
        )}
        <Box
          sx={{
            display: [showMobileSearchButton ? "flex" : "none", null, "flex"],
            flexDirection: "column"
          }}
        >
          <Button
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault(); // Prevents form submission and page reload
              handleSearchSubmit();
            }}
          >
            Go
          </Button>
        </Box>
      </Box>
      {preSearchPrompt && !searchValue && showSearchPrompt && (
        <>
          <Divider color="color-current-brand-800" thickness="sm" sx={{ margin: () => "4px 0" }} />
          <Text
            size="font-size-4"
            sx={{
              color: "color-current-brand-800",
              fontWeight: "font-weight-medium",
              textAlign: "center",
              lineHeight: "leading-2",
              width: () => "80%",
              margin: () => "0 auto"
            }}
          >
            {preSearchPrompt}
          </Text>
        </>
      )}
    </Box>
  );
};
